import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Box,
  Text,
  Flex,
  Icon,
  VStack,
  useDisclosure,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  SlideFade,
  Image
} from '@chakra-ui/react';
import { Sliders } from 'lucide-react';
import Balance from '../../assets/balance-icon.svg';
import ComedyMask from '../../assets/comedy-mask.svg';
import CloseModalIcon from '../../assets/close-modal-icon.svg';
import ApprovalDelegation from '../../assets/approval-delegation.svg';
import WandStars from '../../assets/wand-stars-white.svg';
import Category from '../../assets/category-icon.svg';
import { useUserPreferences } from './hooks/useUserPreferences';
import { WeightsDto, UserFeedPreference } from '../../apis/user-preferences';
import { presetToWeights, createDefaultWeights } from './utils/presetUtils';
import { SavePresetDialog } from './components/SavePresetDialog';
import { PresetSettings } from './components/PresetSettings';
import { ConfigPreset } from './components/ConfigPreset';
import { CreatePreset } from './components/CreatePreset';
import { algorithmSettings } from './constants';
import Button from '../Button';

interface FeedSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function FeedSettingsModal({ open, onOpenChange }: FeedSettingsModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentWeights, setCurrentWeights] = useState<WeightsDto | null>(null);
  const [pendingWeightUpdates, setPendingWeightUpdates] = useState<WeightsDto | null>(null);
  const [currentFeed, setCurrentFeed] = useState<
    'presetSettings' | 'configPreset' | 'createPreset'
  >('presetSettings');
  const [description, setDescription] = useState('');
  const [presetName, setPresetName] = useState('');
  const [selectedPreset, setSelectedPreset] = useState<UserFeedPreference | null>(null);
  const [manualWeights, setManualWeights] = useState<Record<string, number>>({});
  const [aiSuggestedWeights, setAiSuggestedWeights] = useState<Record<string, number>>({});
  const { isOpen: isSaveDialogOpen, onClose: onSaveDialogClose } = useDisclosure();

  const {
    defaultPresets,
    userPresets,
    activePreset,
    updatePresetWeights,
    setActiveFromDefault,
    setActiveFromUserPreset,
    saveCurrentAsPreset,
    deleteUserPreset,
    isLoading,
    createUserPreset,
    createUserPresetWithComponents,
    updatePresetName
  } = useUserPreferences();

  // Debounced weight update
  useEffect(() => {
    if (!pendingWeightUpdates || !selectedPreset) return;

    const timeoutId = setTimeout(() => {
      updatePresetWeights.mutate(
        { presetId: selectedPreset.id, weights: pendingWeightUpdates },
        {
          onSuccess: () => {
            userPresets.refetch();
          }
        }
      );
      setPendingWeightUpdates(null);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [pendingWeightUpdates, updatePresetWeights, selectedPreset, userPresets, defaultPresets]);

  // Wrap updatePresetName to update local state
  const handleUpdatePresetName = (params: { presetId: string; name: string }) => {
    updatePresetName.mutate(params, {
      onSuccess: () => {
        if (selectedPreset && selectedPreset.id === params.presetId) {
          setSelectedPreset({ ...selectedPreset, name: params.name });
        }
      }
    });
  };

  const getPresetIcon = (preset: UserFeedPreference) => {
    // Default presets get specific icons
    if (!preset.userId) {
      switch (preset.name.toLowerCase()) {
        case 'balanced':
          return <img src={Balance} alt="" className="w-4 h-4" />;
        case 'light & fun':
          return <img src={ComedyMask} alt="" className="w-4 h-4" />;
        case 'serious':
          return <img src={ApprovalDelegation} alt="" className="w-4 h-4" />;
        default:
          return <img src={Category} className="h-4 w-4" />;
      }
    }
    // Custom presets get the Category icon
    return <img src={Category} className="h-4 w-4" />;
  };

  const handlePresetSelect = (preset: UserFeedPreference) => {
    const newWeights = presetToWeights(preset);
    setCurrentWeights(newWeights);
    setPendingWeightUpdates(null); // Cancel any pending updates

    if (preset.userId) {
      // Custom preset
      setActiveFromUserPreset.mutate(preset.id);
    } else {
      // Default preset
      setActiveFromDefault.mutate(preset.id);
    }
  };

  const handleWeightChange = useCallback(
    (settingId: keyof WeightsDto, value: number) => {
      if (!currentWeights) return;

      const newWeights = { ...currentWeights, [settingId]: value };
      setCurrentWeights(newWeights);
      setPendingWeightUpdates(newWeights);
    },
    [currentWeights]
  );

  const handleSavePreset = (name: string, description?: string) => {
    saveCurrentAsPreset.mutate(
      { name, description, instruction: description },
      {
        onSuccess: (newPreset) => {
          // Automatically activate the newly saved preset
          setActiveFromUserPreset.mutate(newPreset.id, {
            onSuccess: () => {
              onSaveDialogClose();
            },
            onError: () => {
              // If activation fails, still close the dialog
              onSaveDialogClose();
            }
          });
        }
      }
    );
  };

  const handleDeletePreset = (presetId: string) => {
    deleteUserPreset.mutate(presetId);
  };

  const handleBackToFeed1 = () => {
    setCurrentFeed('presetSettings');
    setCurrentWeights(null);
    setSelectedPreset(null);
    // Clear form state when going back
    setDescription('');
    setPresetName('');
    setManualWeights({});
    setAiSuggestedWeights({});
  };

  const handleCreateNewMode = () => {
    // Clear form state when starting new mode creation
    setDescription('');
    setPresetName('');
    setManualWeights({});
    setAiSuggestedWeights({});
    setCurrentFeed('createPreset');
  };

  const handleViewSettings = (preset: UserFeedPreference, isCustom: boolean) => {
    setSelectedPreset(preset);
    const weights = presetToWeights(preset);
    setCurrentWeights(weights);
    setCurrentFeed('configPreset');
  };

  const handleCloseModal = () => {
    onOpenChange(false);
    // Clear all form state when closing modal
    setDescription('');
    setPresetName('');
    setManualWeights({});
    setAiSuggestedWeights({});
    setCurrentWeights(null);
    setSelectedPreset(null);
    setCurrentFeed('presetSettings');
  };

  const handleManualWeightChange = (weightKey: string, value: number) => {
    setManualWeights(prev => ({
      ...prev,
      [weightKey]: value
    }));
  };

  const handleAiWeightsUpdate = (weights: Record<string, number>) => {
    setAiSuggestedWeights(weights);
  };

  const handleSubmit = () => {
    if (!description.trim()) {
      return;
    }

    // Start with default weights, then apply AI suggestions, then manual adjustments
    const baseWeights = createDefaultWeights();
    const finalWeights = { ...baseWeights, ...aiSuggestedWeights, ...manualWeights };
    
    console.log('Final weights being sent:', finalWeights);
    console.log('Manual weights:', manualWeights);
    console.log('AI suggested weights:', aiSuggestedWeights);
    console.log('Base weights:', baseWeights);

    createUserPresetWithComponents.mutate(
      {
        name: presetName.trim() || undefined,
        description: description.trim(),
        instruction: description.trim(),
        weights: finalWeights
      },
      {
        onSuccess: (newPreset) => {
          // Automatically activate the newly created preset
          setActiveFromUserPreset.mutate(newPreset.id, {
            onSuccess: () => {
              // Clear input fields
              setDescription('');
              setPresetName('');
              setManualWeights({});
              setAiSuggestedWeights({});
              handleBackToFeed1();
            },
            onError: () => {
              // If activation fails, still clear the form and go back
              setDescription('');
              setPresetName('');
              setManualWeights({});
              setAiSuggestedWeights({});
              handleBackToFeed1();
            }
          });
        }
      }
    );
  };

  const filteredUserPresets =
    userPresets.data?.filter(
      (preset) =>
        preset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        preset.description?.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];

  const filteredDefaultPresets = {
    ...defaultPresets,
    data: defaultPresets.data?.filter(
      (preset) =>
        preset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        preset.description?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  };

  if (isLoading) {
    return (
      <Modal isOpen={open} onClose={handleCloseModal} size="xl">
        <ModalOverlay />
        <ModalContent maxH="95vh" maxW="70vw" w="50vw" mt={6}>
          <ModalBody>
            <Center py={20}>
              <VStack spacing={4}>
                <Spinner size="xl" color="green.500" />
                <Text>Loading feed settings...</Text>
              </VStack>
            </Center>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  if (activePreset.error || defaultPresets.error || userPresets.error) {
    return (
      <Modal isOpen={open} onClose={handleCloseModal} size="xl">
        <ModalOverlay />
        <ModalContent maxH="95vh" maxW="70vw" w="50vw" mt={6}>
          <ModalBody>
            <Alert status="error">
              <AlertIcon />
              Failed to load feed settings. Please try again.
            </Alert>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <>
      <Modal isOpen={open} onClose={handleCloseModal} size={{ base: 'full', md: 'xl' }}>
        <ModalOverlay />
        <ModalContent 
          maxH={{ base: '100vh', md: '95vh' }}
          maxW={{ base: '100vw', md: '70vw' }}
          w={{ base: '100vw', md: '812px' }}
          h={{ base: '100vh', md: '910px' }}
          mt={{ base: '0', md: '5' }}
          mb="0"
          borderRadius={{ base: '0', md: 'md' }}
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <ModalHeader 
            px={{ base: 3, md: 4 }} 
            py={{ base: 3, md: 4 }} 
            className="m-0 mx-[8px] max-w-full"
            flexShrink={0}
            style={{ touchAction: 'none' }}
          >
            <Box className="w-full border-b pb-[7px]">
              <Flex
                alignItems="center"
                justifyContent="space-between"
                gap={2}
              >
                <Flex alignItems="center" gap={2}>
                  <Icon as={Sliders} color="green.500" className="rotate-90" />
                  <Text fontWeight={800} fontSize={{ base: 18, md: 20 }}>
                    Feed Settings
                  </Text>
                </Flex>
                <Image
                  className="cursor-pointer"
                  w={{ base: '24px', md: '30px' }}
                  src={CloseModalIcon}
                  alt="Close"
                  onClick={handleCloseModal}
                />
              </Flex>
            </Box>
          </ModalHeader>

          <ModalBody 
            overflowY="auto"
            px={{ base: 3, md: 6 }} 
            py={{ base: 3, md: 4 }}
            flex={1}
            style={{ 
              touchAction: 'pan-y',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            <Box position="relative" minH={{ base: 'auto', md: '600px' }}>
              <SlideFade in={currentFeed === 'presetSettings'} offsetY="20px" unmountOnExit>
                <Box position="absolute" width="100%">
                  <PresetSettings
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    defaultPresets={filteredDefaultPresets}
                    userPresets={{ ...userPresets, data: filteredUserPresets }}
                    activePreset={activePreset}
                    getPresetIcon={getPresetIcon}
                    handlePresetSelect={handlePresetSelect}
                    handleViewSettings={handleViewSettings}
                    handleCreateNewMode={handleCreateNewMode}
                    handleDeletePreset={handleDeletePreset}
                  />
                </Box>
              </SlideFade>

              <SlideFade in={currentFeed === 'configPreset'} offsetY="20px" unmountOnExit>
                <Box position="absolute" width="100%">
                  <ConfigPreset
                    currentWeights={currentWeights}
                    handleWeightChange={handleWeightChange}
                    handleBackToFeed1={handleBackToFeed1}
                    algorithmSettings={algorithmSettings}
                    selectedPreset={selectedPreset}
                    getPresetIcon={getPresetIcon}
                    updatePresetName={handleUpdatePresetName}
                  />
                </Box>
              </SlideFade>

              <SlideFade in={currentFeed === 'createPreset'} offsetY="20px" unmountOnExit>
                <Box position="absolute" width="100%">
                  <CreatePreset
                    userPresets={userPresets}
                    activePreset={activePreset}
                    getPresetIcon={getPresetIcon}
                    handlePresetSelect={handlePresetSelect}
                    handleBackToFeed1={handleBackToFeed1}
                    description={description}
                    setDescription={setDescription}
                    presetName={presetName}
                    setPresetName={setPresetName}
                    manualWeights={manualWeights}
                    onWeightChange={handleManualWeightChange}
                    onAiWeightsUpdate={handleAiWeightsUpdate}
                    onSubmit={handleSubmit}
                  />
                </Box>
              </SlideFade>
            </Box>
          </ModalBody>

          <ModalFooter 
            flexShrink={0}
            style={{ touchAction: 'none' }}
          >
            {currentFeed === 'presetSettings' && (
              <Flex width="100%" justify="flex-end" align="center">
                <Button size="sm" h="36px" minW="90px" onClick={handleCloseModal} variant="primary">
                  Done
                </Button>
              </Flex>
            )}
            {currentFeed === 'configPreset' && (
              <Flex width="100%" justify="flex-end" align="center">
                <Button size="sm" h="36px" minW="90px" onClick={handleCloseModal} variant="primary">
                  Done
                </Button>
              </Flex>
            )}
            {currentFeed === 'createPreset' && (
              <Flex width="100%" justify="flex-end" align="center" gap={2}>
                <Button
                  size="sm"
                  h="36px"
                  minW="90px"
                  onClick={handleSubmit}
                  variant="primary"
                  isLoading={createUserPresetWithComponents.isPending}
                  className="flex flex-row items-center gap-2"
                >
                  Generate Mode
                  <img src={WandStars} alt="" />
                </Button>
              </Flex>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <SavePresetDialog
        isOpen={isSaveDialogOpen}
        onClose={onSaveDialogClose}
        onSave={handleSavePreset}
        isLoading={saveCurrentAsPreset.isPending}
      />
    </>
  );
}
